import { Component, EventEmitter, Input, OnChanges, OnDestroy, Optional, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, of } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import type { ExplainerPlacement } from '../report-design-edit';
import {
  ColorPalette,
  DEFAULT_COLOR_PALETTE,
  PageSection,
  ReportStatistics,
  ReportType,
} from '../../../models/end-client-reporting-package';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ModuleGuideModalComponent } from '../module-guide-modal.component';
import type { CrosstabExtraData } from '../../../models/end-client-reporting-component-types/crosstab';
import { resolveStatistics } from '../crosstab-stats';
import {
  AnswerOption,
  ChartSeries,
  PickableVariable,
  offScaleAnswers,
  acceptsGroupBy,
  acceptsMultipleVariables,
  blockGroupByKey,
  blockVariableKeys,
} from '../report-aggregation';
import { BlockField, BlockPatch, BlockWidth, PartVariable, fieldAppliesTo } from '../report-design-edit';
import {
  CHART_ORIENTATION_LABELS,
  ChartOrientation,
  REPORT_BLOCK_ORIENTATIONS,
  defaultOrientationFor,
} from './chart-layout';
import {
  DEFAULT_TIER_LABELS,
  MAX_TIERS,
  tierCountFor,
} from './ranked-driver-block.component';
import { DEFAULT_TOP_RANKS } from './rank-shares';
import { EXAMPLE_STAGE_LABELS } from './block-examples';
import { rungLabels } from './relationship-ladder-block.component';
import { REPORT_BLOCK_GUIDANCE, REPORT_BLOCK_LABELS, groupedReportTypes } from './report-block-registry';
import { ReportSelectGroup, ReportSelectOption } from '../report-select.component';
import { HeatmapImageStore } from './heatmap-images';

/** The same limit the server holds a heatmap image to, checked here so a big file fails at once. */
const MAX_IMAGE_BYTES = 10_000_000;

/** A question as the deck's dropdown wants it: the Decipher position as the code, its text after. */
function toOption(variable: PickableVariable): ReportSelectOption {
  return { value: variable.key, code: variable.position, text: variable.label };
}

/**
 * Puts back a tick the list refused to lose. The browser has already cleared the box by the time the
 * change arrives, and a refused change leaves the `[checked]` binding where it was, so Angular never
 * redraws it -- without this the box reads as unticked while the block still includes it.
 */
function keepTicked(box: HTMLInputElement): void {
  box.checked = true;
}

const CIRCLE_COMFORTABLE_OPTIONS = 6;

/** Kept with the module that draws the chips, which is where the number is explained. */
const DEFAULT_NET_THRESHOLD = 50;

/**
 * How many charted questions before the list starts closed. A ranked bar of a whole battery is a
 * dozen rows, which buries Placement, Orientation and everything under them; three or four is
 * shorter than the heading that would replace it.
 */
const AUTO_COLLAPSE_QUESTIONS = 4;

/** The panel's four groups, in the order they are drawn. */
type ConfigGroup = 'data' | 'scoring' | 'layout' | 'text';

/** How many answers a stage claims. The card reads a stored width the same way. */
function stageWidth(stage: { rungs: number } | undefined): number {
  return Math.max(0, Math.trunc(stage?.rungs ?? 0));
}

/** An option's name without its explanation -- "Gauge", not "Gauge -- the ring, with...". */
function optionName(options: ReportSelectOption[], value: string | null | undefined): string {
  const text = options.find(option => option.value === value)?.text ?? '';
  return text.split(' -- ')[0];
}

/**
 * The settings of one module, in the deck's side panel. The deck renders one of these for the card
 * whose pen was pressed and hands it that card's block, so every card carries only its small
 * toolbar (ReportBlockActionsComponent) and the settings have the panel's full height.
 *
 * Grouped the same way for every module -- Data, Scoring, Layout, Text -- so a setting is always
 * found in the same place, and a group this module has nothing in is left out.
 *
 * Every control is driven off the registry -- the type list, which types offer an orientation,
 * which take a grouping question, which take several variables -- so a new module shows up here
 * without this component being touched.
 */
@Component({
  selector: 'app-report-block-editor',
  standalone: false,
  templateUrl: './report-block-editor.component.html',
  styleUrls: ['./report-block-editor.component.scss'],
})
export class ReportBlockEditorComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) block!: PageSection;
  /** Ladder only. Read back off where the explainer sits -- nothing stores it. */
  @Input() explainerPlacement: ExplainerPlacement = 'none';
  /** Every question in the survey, mapped ones first. Computed once by the deck, not per card. */
  @Input({ required: true }) variables: PickableVariable[] = [];
  /**
   * What the card is actually drawing, so a setting about the answer list can name the answers.
   *
   * Required, like `optionsOf` below, on purpose: an optional input the deck forgot to pass fails
   * silently, as a control that renders and does nothing.
   */
  @Input({ required: true }) series: ChartSeries[] = [];
  /** Supplied by the deck, which is the only thing holding the records the answers come from. */
  @Input({ required: true }) optionsOf!: (variableKey: string) => string[];
  /** A question's answers with their codes, for telling a scale's answers from "Don't know". */
  @Input() layoutOf?: (variableKey: string) => AnswerOption[] | null;
  /** Explainer only: its band comes from the ladder it explains, so it is not editable here. */
  @Input() contentDerived = false;
  /** The deck's statistics, which a crosstab's tests are run by and so names. */
  @Input() statistics?: ReportStatistics;
  /** The deck's colours, so the module guide's examples are drawn in them. */
  @Input() palette?: ColorPalette;

  @Output() patch = new EventEmitter<BlockPatch>();
  @Output() variableChange = new EventEmitter<PartVariable>();
  @Output() variablesChange = new EventEmitter<PartVariable[]>();
  /** Separate from `patch` because a grouping question has to be recorded in variableMap too. */
  @Output() groupByChange = new EventEmitter<PartVariable>();
  /** Same again for the ownership matrix's priority column, which names a whole ranking grid. */
  @Output() priorityChange = new EventEmitter<PartVariable[]>();
  /**
   * And so does every question the panel list names -- a second question added here may never have
   * been charted, and an unmapped key resolves to no answers at all.
   */
  @Output() subgroupsChange = new EventEmitter<{
    subgroups: { variableKey: string; answer: string }[];
    variables: PartVariable[];
  }>();
  /** Crosstab only: the same table again for each of these questions, under this one. */
  @Output() copyForQuestions = new EventEmitter<PartVariable[]>();
  /** Crosstab only: open the Report statistics dialog, which its tests are run by. */
  @Output() openStatistics = new EventEmitter<void>();
  /** Crosstab only: its third variable, recorded in variableMap like the grouping question. */
  @Output() layerChange = new EventEmitter<PartVariable | null>();
  /** Crosstab only: the respondents its own filter keeps, with the questions it names to map. */
  @Output() tableFilterChange = new EventEmitter<{
    tableFilter: { variableKey: string; answer: string }[];
    variables: PartVariable[];
  }>();
  @Output() explainerPlacementChange = new EventEmitter<ExplainerPlacement>();
  @Output() typeChange = new EventEmitter<ReportType>();
  /** Full or half. Which side a half sits on is where it is dragged to, so it is not asked here. */
  @Output() widthChange = new EventEmitter<BlockWidth>();
  /** The panel's Close. Every change has already gone out through patch, to be saved with the deck. */
  @Output() closed = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();
  private readonly heatmapQuestion$ = new Subject<string>();

  /** Whether the survey has its own image for the heatmap's question, which decides if one can be uploaded. */
  surveyImage: 'pending' | 'found' | 'none' = 'pending';

  // Optional: drawn where no page provides the store, the panel simply has nowhere to upload a
  // heatmap image to.
  constructor(
    @Optional() protected images: HeatmapImageStore | null = null,
    @Optional() private modals: BsModalService | null = null,
  ) {
    this.heatmapQuestion$.pipe(
      distinctUntilChanged(),
      switchMap(question => {
        this.surveyImage = 'pending';
        return question && images ? images.imageFor(question) : of(null);
      }),
      takeUntil(this.destroy$),
    ).subscribe(url => { this.surveyImage = url ? 'found' : 'none'; });
  }

  /**
   * The module guide, open on this module. Choosing another there switches this one, through the
   * same event the picker uses -- so it converts the module exactly as picking it would.
   */
  openGuide() {
    if (!this.modals) return;
    const initialState: Partial<ModuleGuideModalComponent> = {
      selected: this.block.reportType,
      current: this.block.reportType,
      palette: this.palette ?? DEFAULT_COLOR_PALETTE,
      choose: type => this.typeChange.emit(type),
    };
    this.modals.show(ModuleGuideModalComponent, {
      initialState,
      class: 'modal-xl modal-dialog-centered',
      ariaLabelledBy: 'guide-title',
    });
  }

  ngOnChanges() {
    if (this.block.reportType !== 'heatmap') return;
    this.heatmapQuestion$.next(this.series.find(series => series.heatmap)?.heatmap?.question ?? '');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Which groups are open. Kept on the panel rather than per module, so opening Layout on one card
   * leaves it open on the next -- someone going down a deck setting widths wants the same group
   * each time. Data and Scoring open first: they are what a module is picked and set up by.
   */
  private readonly openGroups = new Set<ConfigGroup>(['data', 'scoring']);

  isOpen(group: ConfigGroup): boolean {
    return this.openGroups.has(group);
  }

  toggleGroup(group: ConfigGroup) {
    if (this.openGroups.has(group)) this.openGroups.delete(group);
    else this.openGroups.add(group);
  }

  get isCrosstab(): boolean {
    return this.block.reportType === 'crosstab';
  }

  /**
   * What each group is called. The same four for every module, so a setting is always in the same
   * place -- but a table is set up as a table: what goes in it, how it is tested, how it reads.
   */
  groupName(group: ConfigGroup): string {
    if (this.isCrosstab) {
      // Not "Statistics": that is the report-level panel, and two things under one name is a hunt.
      return { data: 'Table', scoring: 'Tests & nets', layout: 'Display', text: 'Text' }[group];
    }
    return { data: 'Data', scoring: 'Scoring', layout: 'Layout', text: 'Text' }[group];
  }

  /** Whether this module has anything to work out beyond counting -- bands, a net, a ranking. */
  get hasScoring(): boolean {
    return this.showsBands
      || this.takesStages
      || (this.isDial && this.showsScale)
      || this.showsRankedDriver
      || this.takesMatrix
      || this.showsLeftAnswer
      || this.showsTableStats
      || this.showsValueUnit
      || this.showsCountedAnswers
      || this.showsDropOff
      || this.showsNet;
  }

  /**
   * Whether this module has words to set here. Most are typed straight onto the card -- column
   * headings, a band's captions, a single bar's caption -- so what is left is wording drawn inside a
   * chart, where there is nowhere to type, and wording that is built from what is set here rather
   * than shown as typed.
   */
  get hasText(): boolean {
    return this.takesAxes
      || this.takesStages
      || this.showsSpectrum
      || this.showsNetWord
      || (this.showsNet && (this.netOn || (this.shareOn && !this.singleBar)));
  }

  /** What each group is set to, for its heading while it is closed. */
  get summaries(): Record<ConfigGroup, string> {
    return {
      data: this.dataSummary,
      scoring: this.scoringSummary,
      layout: this.layoutSummary,
      text: this.textSummary,
    };
  }

  private get dataSummary(): string {
    if (this.isImageModule) return this.imageId ? 'Image uploaded' : 'No image yet';
    if (this.chartsNothing && !this.isDial) return 'Edited on the module itself';
    const read = this.takesAxes
      ? [this.axisKey(0), this.axisKey(1)].filter(Boolean).map(key => this.labelFor(key)).join(' vs ')
      : this.takesGrid
        ? this.chartedGrid
        : this.chartedKeys.length > 1
          ? this.questionCountLabel
          : this.labelFor(this.chartedKeys[0] ?? '');
    const parts = [read || 'No question chosen yet'];
    if (this.takesGroupBy && this.groupByKey) parts.push(`by ${this.labelFor(this.groupByKey)}`);
    if (this.priorityGrid) parts.push(`priority from ${this.priorityGrid}`);
    if (this.showsHeatmap) parts.push(this.imageId ? 'Uploaded image' : 'Survey\'s image');
    if (this.layerBy) parts.push(`split by ${this.labelFor(this.layerBy)}`);
    if (this.tableFilter.length) parts.push('Filtered');
    return parts.join(' · ');
  }

  private get scoringSummary(): string {
    const parts: string[] = [];
    const bandCount = this.showsBands ? this.bands.length : 0;
    if (bandCount) parts.push(`${bandCount} band${bandCount === 1 ? '' : 's'}`);
    const stageCount = this.takesStages ? this.stages.length : 0;
    if (stageCount) parts.push(`${stageCount} stage${stageCount === 1 ? '' : 's'}`);
    if (this.isDial && this.showsBands) parts.push(optionName(this.metricOptions, this.metric));
    if (this.showsRankedDriver || this.priorityGrid) parts.push(`Top ${this.topRanks}`);
    if (this.showsRankedDriver && this.tierCount > 1) parts.push(`${this.tierCount} tiers`);
    if (this.showsCountedAnswers) parts.push(this.countedAnswerLabel());
    if (this.showsNet && this.netSummary !== 'Off') parts.push(this.netSummary);
    if (this.takesMatrix && this.showWhiteSpace) parts.push('What nobody owns');
    if (this.showsTableStats && this.sigTest !== 'none') parts.push(this.sigTest === 'columns' ? 'Columns tested' : 'Tested against the rest');
    if (this.showsTableStats && this.nets.length) parts.push(`${this.nets.length} net${this.nets.length === 1 ? '' : 's'}`);
    if (this.showsTableStats && this.showMean) parts.push('Mean');
    return parts.join(' · ');
  }

  private get layoutSummary(): string {
    const parts = [optionName(this.widthOptions, this.width)];
    if (this.orientations.length) parts.push(this.orientationLabels[this.orientation]);
    if (this.isDial) parts.push(optionName(this.displayOptions, this.display));
    if (this.showsSorting && this.sortDescending) parts.push('Sorted');
    if (this.showsTopN && this.topN) parts.push(`Top ${this.topN}`);
    if (this.showsHideLabels && this.hideLabels) parts.push('No labels');
    if (this.showsCellValue) parts.push(optionName(this.cellValueOptions, this.cellValue));
    if (this.showsTableStats && this.shading !== 'none') parts.push(optionName(this.shadingOptions, this.shading));
    return parts.filter(Boolean).join(' · ');
  }

  private get textSummary(): string {
    const named = [
      this.xLabel, this.yLabel, this.quadrantLabel, this.netLabel,
      ...(this.shareOn && !this.singleBar ? [this.shareCaption] : []),
      ...(this.showsSpectrum ? [this.spectrumText] : []),
      ...(this.takesStages ? [this.stageTotalLabel] : []),
    ].filter(text => text.trim()).length;
    return named ? `${named} named` : 'Using the defaults';
  }

  readonly moduleGroups = groupedReportTypes();
  readonly guidance = REPORT_BLOCK_GUIDANCE;
  readonly orientationLabels = CHART_ORIENTATION_LABELS;
  readonly widthOptions: ReportSelectOption[] = [
    { value: 'full', text: 'Full width' },
    { value: 'half', text: 'Half width' },
  ];

  /** The block's width, whichever side a half is on. */
  get width(): BlockWidth {
    return this.block.segmentLocation === 'full' ? 'full' : 'half';
  }

  setWidth(value: string) {
    if (value === this.width || (value !== 'full' && value !== 'half')) return;
    this.widthChange.emit(value);
  }

  /**
   * Only the questions this module can actually chart. A numeric battery is a set of scores, so it
   * has no slices to fill a circle and no options to break a chart into panels -- offering it there
   * would let someone build a module that can only render nothing.
   */
  get chartable(): PickableVariable[] {
    // An open-end is the only question the verbatim explorer can read, and the only one no other
    // module can: there is nothing in a paragraph to count, so offering one anywhere else is
    // offering a chart that renders nothing.
    if (this.takesText) return this.variables.filter(variable => variable.kind === 'text');
    // The same holds for image-highlighter questions and the heatmap, in both directions.
    if (this.takesHeatmap) return this.variables.filter(variable => variable.kind === 'heatmap');
    return this.variables.filter(variable => variable.kind !== 'text' && variable.kind !== 'heatmap'
      && (variable.kind !== 'numeric' || this.takesNumeric));
  }

  get takesHeatmap(): boolean {
    return this.block.reportType === 'heatmap';
  }

  /** The heatmap's own settings, once it has a question to draw. */
  get showsHeatmap(): boolean {
    return this.takesHeatmap && !!this.chartedKeys.length;
  }

  get imageId(): string {
    return this.block.reportType === 'heatmap' || this.block.reportType === 'image'
      ? this.block.reportExtraData.imageId ?? ''
      : '';
  }

  /** The Image module: a picture to upload and describe, in place of a question to pick. */
  get isImageModule(): boolean {
    return this.block.reportType === 'image';
  }

  get altText(): string {
    return this.block.reportType === 'image' ? this.block.reportExtraData.altText ?? '' : '';
  }

  /**
   * Only onto a report, since a template has nowhere to keep an image -- and only where the survey
   * has none of its own. The survey's is the file the boxes were drawn on, so an upload beside it
   * could only be a way of getting the image wrong.
   */
  get canUploadImage(): boolean {
    return !!this.images?.canUpload && this.surveyImage === 'none';
  }

  imageUploading = false;
  /** What went wrong with the last upload, kept with the block it was for. */
  imageError: { blockId: string; text: string } | null = null;

  chooseImage(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Cleared so choosing the same file again, after a failure, still counts as a change.
    input.value = '';
    if (!file || !this.images) return;

    const blockId = this.block.id;
    if (file.size > MAX_IMAGE_BYTES) {
      this.imageError = { blockId, text: 'Images can be up to 10 MB.' };
      return;
    }

    this.imageUploading = true;
    this.imageError = null;
    this.images.upload(file).pipe(takeUntil(this.destroy$)).subscribe({
      next: imageId => {
        this.imageUploading = false;
        // The panel swaps blocks in place, and `patch` goes to whichever is open now.
        if (this.block.id === blockId) this.patch.emit({ imageId });
      },
      error: (failure: HttpErrorResponse) => {
        this.imageUploading = false;
        const message = (failure.error as { message?: string } | null)?.message;
        this.imageError = { blockId, text: message ?? 'The image could not be uploaded. Try again.' };
      },
    });
  }

  useSurveyImage() {
    this.patch.emit({ imageId: '' });
  }

  get showOptOut(): boolean {
    return this.block.reportType === 'heatmap' && this.block.reportExtraData.showOptOut !== false;
  }

  /** Only a question with more than one click category has panels to put on one scale. */
  get showsScaleChoice(): boolean {
    return this.showsHeatmap
      && (this.variables.find(variable => variable.key === this.chartedKeys[0])?.optionCount ?? 0) > 1;
  }

  get sharedScale(): boolean {
    return this.block.reportType === 'heatmap' && !!this.block.reportExtraData.sharedScale;
  }

  /** Bars are the only shape a mean per item reads as. */
  private get takesNumeric(): boolean {
    return this.block.reportType === 'bar-chart';
  }

  /** The modules that read what people wrote rather than counting what they picked. */
  get takesText(): boolean {
    return this.block.reportType === 'open-end-explorer' || this.block.reportType === 'word-cloud';
  }

  /** The cloud's own settings; the rest of the text controls belong to the explorer. */
  get showsWordCloud(): boolean {
    return this.block.reportType === 'word-cloud' && !!this.chartedKeys.length;
  }

  get showsVerbatimList(): boolean {
    return this.block.reportType === 'open-end-explorer';
  }

  /**
   * The explainer control, on the ladder's settings because the ladder is what it reads.
   *
   * Unlike everything else in this panel it is not a setting on the block: choosing a placement
   * creates, moves or destroys a second block, and the current value is read back off where that
   * block sits. So it leaves as its own output rather than through `patch`, which only ever
   * rewrites this block's own reportExtraData.
   */
  get showsExplainerPlacement(): boolean {
    return this.block.reportType === 'stage-matrix';
  }

  /**
   * The direction band, for the two modules that draw one -- only where there are no stages or
   * panels for its captions to sit under, since with them each caption is typed onto the card under
   * its own. A paired explainer is left out: its band is read off its ladder on every render, so
   * anything typed here would be discarded.
   */
  get showsSpectrum(): boolean {
    if (this.block.reportType === 'stage-matrix') return !this.stages.length;
    return this.block.reportType === 'stage-explainer' && !this.contentDerived
      && !this.block.reportExtraData.panels?.length;
  }

  get spectrumText(): string {
    const extra = this.block.reportExtraData as { spectrum?: string[] };
    return (extra.spectrum ?? []).join('\n');
  }

  setSpectrum(text: string) {
    this.patch.emit({ spectrum: text.split('\n') } as BlockPatch);
  }

  readonly explainerPlacementOptions: ReportSelectOption[] = [
    { value: 'none', text: 'No explainer' },
    { value: 'before', text: 'On the page before' },
    { value: 'after', text: 'On the page after' },
  ];

  /** The module picker, keeping the registry's own grouping. These have no code, only a name. */
  get moduleOptions(): ReportSelectGroup[] {
    return this.moduleGroups.map(group => ({
      label: group.label,
      options: group.types.map(type => ({ value: type.value, text: type.label })),
    }));
  }

  /**
   * Questions, split the way the old optgroups were. One row per question -- a multi-punch
   * question's options and a battery's items are not offered here, because picking the question
   * already offers them as a tick list, and listing them twice put 166 rows in front of everyone
   * to serve a case that is better served after the question is chosen.
   */
  get questionGroups(): ReportSelectGroup[] {
    const questions = this.chartable.filter(variable => !variable.partOf);
    const loose = questions.filter(variable => !variable.group);

    // First seen wins the order, so the grids come out in the order they were asked -- `variables`
    // is already in data position order.
    const grids = new Map<string, PickableVariable[]>();
    for (const variable of questions) {
      if (!variable.group) continue;
      const rows = grids.get(variable.group);
      if (rows) rows.push(variable);
      else grids.set(variable.group, [variable]);
    }

    return [
      { label: 'In this report', options: loose.filter(v => v.mapped).map(toOption) },
      { label: 'Other questions', options: loose.filter(v => !v.mapped).map(toOption) },
      ...[...grids].map(([label, rows]) => ({ label, options: rows.map(toOption) })),
    ];
  }

  /** null until someone says otherwise, so the default can depend on how long the list turned out. */
  private questionsOpenChoice: boolean | null = null;

  get questionsOpen(): boolean {
    return this.questionsOpenChoice ?? this.chartedKeys.length < AUTO_COLLAPSE_QUESTIONS;
  }

  toggleQuestions() {
    this.questionsOpenChoice = !this.questionsOpen;
  }

  get questionCountLabel(): string {
    const count = this.chartedKeys.length;
    return `${count} question${count === 1 ? '' : 's'}`;
  }

  get groupByOptions(): ReportSelectOption[] {
    return this.groupByChoices.map(toOption);
  }

  get orientationOptions(): ReportSelectOption[] {
    return this.orientations.map(choice => ({ value: choice, text: this.orientationLabels[choice] }));
  }

  /** Every variable this chart draws. One for most types, one per bar for a ranked bar. */
  get chartedKeys(): string[] {
    return blockVariableKeys(this.block);
  }

  get groupByKey(): string {
    return blockGroupByKey(this.block) ?? '';
  }

  get includeTotal(): boolean {
    return this.block.reportType === 'subgroup-panels'
      || this.block.reportType === 'segment-tiles'
      || this.block.reportType === 'crosstab'
      ? this.block.reportExtraData.includeTotal !== false
      : true;
  }

  get orientations(): readonly ChartOrientation[] {
    return REPORT_BLOCK_ORIENTATIONS[this.block.reportType];
  }

  get orientation(): ChartOrientation {
    const data = this.block.reportExtraData as { orientation?: ChartOrientation };
    return data.orientation ?? defaultOrientationFor(this.block.reportType);
  }

  get takesPanels(): boolean {
    return acceptsMultipleVariables(this.block.reportType);
  }

  get takesGroupBy(): boolean {
    return acceptsGroupBy(this.block.reportType);
  }

  get sortDescending(): boolean {
    return 'sortDescending' in this.block.reportExtraData
      && !!this.block.reportExtraData.sortDescending;
  }

  /** Blank rather than 0 when there is no limit, so the field reads as "all of them". */
  get topN(): string {
    const limit = 'topN' in this.block.reportExtraData ? this.block.reportExtraData.topN : undefined;
    return limit && limit > 0 ? String(limit) : '';
  }

  /** Anything that charts a question can be sorted -- unless its order is itself the finding. */
  get showsSorting(): boolean {
    // Asked of the same function that decides whether the patch would be kept, so a control can
    // never be offered for a setting the module quietly drops -- which is what left a stacked
    // distribution showing Sort and Show top, neither of which it has ever honoured.
    // Bands are counted off the ends of the answer list, so sorting would move answers between
    // them. `fieldAppliesTo` is type-level and cannot see that this block has any, so the guard
    // lives here and again in the block, which ignores bands it finds against a reordered scale.
    if (this.hasBands) return false;
    return this.chartedKeys.length > 0 && this.applies('sortDescending');
  }

  /** Whether this block groups its scale, which several other controls stand down for. */
  get hasBands(): boolean {
    return !!(this.block.reportExtraData as { bands?: unknown[] }).bands?.length;
  }

  private applies(field: BlockField): boolean {
    return fieldAppliesTo(this.block.reportType, field);
  }

  /**
   * Hidden where it would do nothing. A single-select pie or donut ignores topN -- the slices left
   * would fill the circle while their labels summed to under 100% -- so offering the control there
   * would be a setting that silently has no effect. It still applies to the donut-per-option grid a
   * multi-answer question renders as.
   */
  get showsTopN(): boolean {
    // Trimming the scale drops answers out of the bands counted off its end -- see showsSorting.
    if (this.hasBands) return false;
    if (!this.chartedKeys.length || !this.applies('topN')) return false;
    const circle = this.block.reportType === 'pie-chart' || this.block.reportType === 'donut-chart';
    if (!circle) return true;
    return this.variables.find(v => v.key === this.chartedKeys[0])?.kind === 'multi';
  }

  /**
   * Only for a numeric battery, and only on a bar chart. A coded question's bars are already
   * percentages of the base and there is nothing to choose.
   */
  get showsValueUnit(): boolean {
    if (this.block.reportType !== 'bar-chart') return false;
    return this.chartedKeys.some(key =>
      this.variables.find(variable => variable.key === key)?.kind === 'numeric');
  }

  get valueUnitOptions(): ReportSelectOption[] {
    return [
      { value: 'score', text: 'A score (55.1)' },
      { value: 'percent', text: 'A percentage (55%)' },
    ];
  }

  get valueUnit(): string {
    return this.block.reportType === 'bar-chart'
      ? this.block.reportExtraData.valueUnit ?? 'score'
      : 'score';
  }

  /** The net column belongs to the one module whose answer order carries meaning. */
  get showsNet(): boolean {
    return this.chartedKeys.length > 0 && this.applies('showNet');
  }

  /** What the net is set to, read from the heading the column will actually carry. */
  get netSummary(): string {
    const label = this.netLabel.trim();
    const parts = [
      ...(this.netOn ? [label ? `Net ${label}` : 'Net'] : []),
      ...(this.shareOn ? ['Share'] : []),
    ];
    return parts.length ? parts.join(' + ') : 'Off';
  }

  /** One bar headlines its scores instead of drawing a column, and only then can it add a share. */
  get singleBar(): boolean {
    return this.chartedKeys.length === 1;
  }

  get shareOn(): boolean {
    return this.block.reportType === 'stacked-scale' && !!this.block.reportExtraData.showShare;
  }

  /**
   * What a battery's right-hand column shows. One column, so one choice rather than two boxes that
   * could both be ticked: the share takes the column where a design has both set.
   */
  get rightColumn(): string {
    if (this.shareOn) return 'share';
    return this.block.reportType === 'stacked-scale' && this.block.reportExtraData.showNet ? 'net' : 'none';
  }

  readonly rightColumnOptions: ReportSelectOption[] = [
    { value: 'none', text: 'Nothing' },
    { value: 'net', text: 'Net -- the top band minus the bottom' },
    { value: 'share', text: "Share -- one band's share of each bar" },
  ];

  setRightColumn(value: string) {
    this.patch.emit({ showNet: value === 'net', showShare: value === 'share' });
  }

  get shareCaption(): string {
    return this.block.reportType === 'stacked-scale'
      ? this.block.reportExtraData.shareCaption ?? ''
      : '';
  }

  /**
   * The caption the figure gets when this is left blank -- the chosen band's name -- so the field
   * shows what blank means.
   */
  /** The line under one bar's figure, or the word after each figure in a battery's column. */
  get shareCaptionHint(): string {
    return this.singleBar
      ? 'The line under the figure, e.g. "say offers are a major reason". Leave blank for the band\'s name.'
      : 'The word after each figure, e.g. "weekly" for 78% weekly. Leave blank for the band\'s name.';
  }

  get shareCaptionPlaceholder(): string {
    const chosen = this.shareBandOptions[Number(this.shareBand)];
    return chosen?.text ?? this.legacyBoxLabel;
  }

  /**
   * A stacked distribution written before bands existed still scores by a symmetric box count. Said
   * back in the net section so the figure is not a mystery, with the way to change it.
   */
  get legacyBoxesNote(): string {
    if (this.block.reportType !== 'stacked-scale' || this.bands.length) return '';
    if (!this.netOn && !this.shareOn) return '';
    const boxes = Math.max(1, Math.floor(this.block.reportExtraData.netBoxes ?? 2));
    return `Counting the top ${boxes} and bottom ${boxes} answers. Set answer bands to change this.`;
  }

  /** What an unbanded block's top end is called, matching the headline it draws. */
  private get legacyBoxLabel(): string {
    const boxes = this.block.reportType === 'stacked-scale'
      ? Math.max(1, Math.floor(this.block.reportExtraData.netBoxes ?? 2))
      : 2;
    return boxes === 1 ? 'Top box' : `Top-${boxes} box`;
  }

  /**
   * Ordering the *bars* -- which statement leads -- is a different question from ordering the
   * answers within a bar, which this module must never do.
   */
  get showsBarOrder(): boolean {
    return this.chartedKeys.length > 1 && this.applies('barOrder');
  }

  /** Ranked by whichever score the column shows: the net's order, or the share's. */
  get barOrderOptions(): ReportSelectOption[] {
    return [
      { value: 'asked', text: 'As listed' },
      { value: 'net-high', text: 'Net, highest first' },
      { value: 'net-low', text: 'Net, lowest first' },
      { value: 'share-high', text: 'Share, highest first' },
      { value: 'share-low', text: 'Share, lowest first' },
    ];
  }

  get barOrder(): string {
    const order = this.block.reportType === 'stacked-scale'
      ? this.block.reportExtraData.barOrder
      : undefined;
    return order && order !== 'asked' ? order : 'asked';
  }

  /** Drawn, not merely set: on a battery the share takes the column where both are. */
  get netOn(): boolean {
    return this.block.reportType === 'stacked-scale' && !!this.block.reportExtraData.showNet
      && (this.singleBar || !this.shareOn);
  }

  /** Only the figure form builds its wording from the word; the dial has its own subheading. */
  get showsNetWord(): boolean {
    return this.isDial && this.display === 'figure';
  }

  get netLabel(): string {
    const data = this.block.reportExtraData as { netLabel?: string };
    return this.applies('netLabel') ? data.netLabel ?? '' : '';
  }

  /**
   * The answer list the net is read off, as the chart sees it: the drawn options in asked order.
   * Shown back so the direction can be checked against the actual wording rather than guessed at.
   */
  private netScale(): string[] {
    const data = this.block.reportExtraData as { netExclude?: string[] };
    const excluded = new Set(this.applies('netExclude') ? data.netExclude ?? [] : []);
    const widest = this.knownSeries.reduce<ChartSeries | null>(
      (best, entry) => entry.points.length > (best?.points.length ?? 0) ? entry : best, null);
    return (widest?.points ?? [])
      .filter(point => point.optionIndex >= 0 && !excluded.has(point.label))
      .map(point => point.label);
  }

  // ---------------------------------------------------------------- answer bands, and what they add
  //
  // Shared by the dial, which reduces a banded scale to one figure, the bar chart, which draws the
  // same scale underneath it, and the stacked distribution, which nets it bar by bar -- so a score
  // and its distribution can be banded alike. The vocabulary is the ladder's: a run of consecutive
  // options per band, counted in asked order.

  /**
   * Only where there is a scale to divide. A block charting nothing has no answer list, and bands
   * authored against nothing cannot be checked by whoever writes them. A stacked battery shares one
   * scale across its bars, so it can be banded however many it draws.
   */
  get showsBands(): boolean {
    const scales = this.chartedKeys.length === 1
      || (this.block.reportType === 'stacked-scale' && this.chartedKeys.length > 0);
    return this.applies('bands') && scales && this.bandScaleLabels.length > 1;
  }

  /** The answers the bands are counted over, in asked order, as the chart sees them. */
  get bandScaleLabels(): string[] {
    return this.netScale();
  }

  get bands(): { label: string; options: number }[] {
    return (this.block.reportExtraData as { bands?: { label: string; options: number }[] }).bands ?? [];
  }

  /** How many answers no band has claimed yet -- what an added band can be given. */
  get unbandedCount(): number {
    const claimed = this.bands.reduce((total, band) => total + Math.max(1, band.options), 0);
    return Math.max(0, this.bandScaleLabels.length - claimed);
  }

  /** The answers a band actually covers, so it can be read back in the survey's own words. */
  bandRange(index: number): string {
    const labels = this.bandScaleLabels;
    let at = 0;
    for (let step = 0; step < index; step++) at += Math.max(1, this.bands[step].options);
    const covered = labels.slice(at, at + Math.max(1, this.bands[index].options));
    if (!covered.length) return '';
    return covered.length === 1 ? covered[0] : covered[0] + '-' + covered[covered.length - 1];
  }

  addBand() {
    if (!this.unbandedCount) return;
    this.patch.emit({ bands: [...this.bands, { label: '', options: 1 }] });
  }

  removeBand(index: number) {
    this.patch.emit({ bands: this.bands.filter((_, at) => at !== index) });
  }

  setBandLabel(index: number, label: string) {
    this.patch.emit({ bands: this.bands.map((band, at) => at === index ? { ...band, label } : band) });
  }

  /**
   * Capped at what is left unclaimed plus this band's own width, so the list can never be widened
   * past the scale -- which blanks the figure rather than shortening a band.
   */
  setBandSize(index: number, value: string) {
    const size = Math.trunc(Number(value));
    if (!Number.isFinite(size) || size < 1) return;
    const ceiling = this.unbandedCount + Math.max(1, this.bands[index].options);
    this.patch.emit({
      bands: this.bands.map((band, at) =>
        at === index ? { ...band, options: Math.min(size, ceiling) } : band),
    });
  }

  // --- The stage matrix -------------------------------------------------------------------------
  //
  // Its stages are answer bands under another name: a name and a run of consecutive answers, laid
  // left to right over the one scale every row shares. So they are set the way bands are, here,
  // and the card is drawn as it will be shown. What is stored has not changed -- `stages`,
  // `totalLabel`, `totalExcludes`, `rungNames` -- so the paired explainer reads them as before.

  /** The matrix's columns: the answers its rows share, in the order asked, as the card draws them. */
  get stageRungs(): string[] {
    if (this.block.reportType !== 'stage-matrix') return [];
    return rungLabels(this.series.filter(series => series.known));
  }

  /** Only once there are columns to group: stages authored against nothing cannot be checked. */
  get takesStages(): boolean {
    return this.stageRungs.length > 0;
  }

  get stages(): { label: string; rungs: number }[] {
    return this.block.reportType === 'stage-matrix'
      ? this.block.reportExtraData.stages ?? []
      : [];
  }

  /** Answers no stage has claimed: drawn ungrouped at the end, and what an added stage is given. */
  get unstagedCount(): number {
    const claimed = this.stages.reduce((total, stage) => total + stageWidth(stage), 0);
    return Math.max(0, this.stageRungs.length - claimed);
  }

  /** The answers a stage covers, in the survey's own words, the way a band's are shown. */
  stageRange(index: number): string {
    let at = 0;
    for (let step = 0; step < index; step++) at += stageWidth(this.stages[step]);
    const covered = this.stageRungs.slice(at, at + stageWidth(this.stages[index]));
    if (!covered.length) return 'Covers nothing: the stages before it take every answer.';
    return covered.length === 1 ? covered[0] : covered[0] + '-' + covered[covered.length - 1];
  }

  /**
   * A new stage takes whatever is unclaimed, which is all it can take: the stages are laid in order
   * over a fixed scale, so one added at the end starts where the last one stopped. Named from the
   * example by position, so a fourth stage of a relationship ladder arrives as "Equity Deficit".
   */
  addStage() {
    const rungs = this.unstagedCount;
    if (!rungs) return;
    const label = EXAMPLE_STAGE_LABELS[this.stages.length] ?? '';
    this.patch.emit({ stages: [...this.stages, { label, rungs }] });
  }

  removeStage(index: number) {
    this.patch.emit({ stages: this.stages.filter((unused, at) => at !== index) });
  }

  setStageLabel(index: number, label: string) {
    this.patch.emit({
      stages: this.stages.map((stage, at) => (at === index ? { ...stage, label } : stage)),
    });
  }

  /**
   * Capped like a band, at what is unclaimed plus the stage's own width, so the stages can never run
   * past the scale. Returns the width kept, which the box is put back to.
   */
  setStageSpan(index: number, value: string): number {
    const current = stageWidth(this.stages[index]);
    const size = Math.trunc(Number(value));
    if (!Number.isFinite(size) || size < 1) return current;
    const kept = Math.min(size, this.unstagedCount + current);
    this.patch.emit({
      stages: this.stages.map((stage, at) => (at === index ? { ...stage, rungs: kept } : stage)),
    });
    return kept;
  }

  /** What the leading column is called. Blank means no leading column at all. */
  get stageTotalLabel(): string {
    return this.block.reportType === 'stage-matrix'
      ? this.block.reportExtraData.totalLabel ?? ''
      : '';
  }

  /** An answer counts toward the leading column until it is taken out. */
  countsTowardTotal(rung: string): boolean {
    return !this.totalExcludes.includes(rung);
  }

  setCountsTowardTotal(rung: string, counts: boolean) {
    const others = this.totalExcludes.filter(label => label !== rung);
    this.patch.emit({ totalExcludes: counts ? others : [...others, rung] });
  }

  private get totalExcludes(): string[] {
    return this.block.reportType === 'stage-matrix'
      ? this.block.reportExtraData.totalExcludes ?? []
      : [];
  }

  /** The gauge, or the score on its own. */
  readonly displayOptions: ReportSelectOption[] = [
    { value: 'dial', text: 'Gauge -- the ring, with the bands beside it' },
    { value: 'figure', text: 'Figure -- the score on its own' },
  ];

  get display(): string {
    const set = this.block.reportType === 'kpi-score'
      ? this.block.reportExtraData.display
      : undefined;
    return set === 'figure' ? 'figure' : 'dial';
  }

  readonly metricOptions: ReportSelectOption[] = [
    { value: 'net', text: 'Net -- the top band minus the bottom' },
    { value: 'share', text: 'Share -- one band on its own' },
  ];

  get metric(): string {
    const set = this.block.reportType === 'kpi-score'
      ? this.block.reportExtraData.metric
      : undefined;
    return set === 'share' ? 'share' : 'net';
  }

  /**
   * The two answers the bands actually reach, which is what the direction is about.
   *
   * Not the ends of the whole scale: a trailing "Not sure" belongs to no band and is counted at
   * neither end, so offering it as the best or the worst answer would name the direction against an
   * answer the net never touches. Bands are walked from the start of the list, so the first covered
   * answer is always the first one asked -- an answer off the scale at the *front* has to be named in
   * `netExclude` instead, which is what that field is for.
   */
  private get bandedEnds(): [string, string] {
    const labels = this.bandScaleLabels;
    const claimed = this.bands.reduce((total, band) => total + Math.max(1, band.options), 0);
    if (!claimed || !labels.length) return [labels[0] ?? '', labels[labels.length - 1] ?? ''];
    return [labels[0], labels[Math.min(claimed, labels.length) - 1]];
  }

  /** Which end of the banded answers the favourable ones sit at. Read wrong, every net inverts. */
  get topBandOptions(): ReportSelectOption[] {
    const [first, last] = this.bandedEnds;
    return [
      { value: 'first', text: first ? first + ' is the best answer' : 'The first answer as asked' },
      { value: 'last', text: last ? last + ' is the best answer' : 'The last answer as asked' },
    ];
  }

  get topBand(): string {
    const set = (this.block.reportExtraData as { topBand?: string }).topBand;
    return set === 'last' ? 'last' : 'first';
  }

  /** Names the band and the answers it covers, because that is what makes the setting checkable. */
  get topBandHint(): string {
    if (!this.bands.length) {
      return 'Which end of the answer list the good answers sit at. The wrong end flips the sign.';
    }
    const at = this.topBand === 'last' ? this.bands.length - 1 : 0;
    const range = this.bandRange(at);
    const name = this.bands[at].label.trim() || 'band ' + (at + 1);
    return range
      ? 'Counting ' + name + ' (' + range + ') as the top of the scale.'
      : 'Counting ' + name + ' as the top of the scale.';
  }

  /** Which band a share reads, named rather than numbered so the choice is legible. */
  get shareBandOptions(): ReportSelectOption[] {
    const ordered = this.topBand === 'last' ? [...this.bands].reverse() : this.bands;
    return ordered.map((band, at) => ({
      value: String(at),
      text: band.label.trim() || 'Band ' + (at + 1),
    }));
  }

  get shareBand(): string {
    const set = this.block.reportType === 'kpi-score'
      || this.block.reportType === 'stacked-scale'
      ? this.block.reportExtraData.shareBand ?? 0
      : 0;
    return String(Math.min(Math.max(0, Math.trunc(set)), Math.max(0, this.bands.length - 1)));
  }

  /**
   * The two ends of the scale, and only where the ring is still a fill against one.
   *
   * A banded dial draws the bands' own shares, which sum to a hundred by construction -- so neither
   * bound moves anything, and showing them offers two settings whose every value looks identical.
   * They belong to the dial that has a figure typed into it, which is the only one still filling an
   * arc from a floor to a score.
   */
  get showsScale(): boolean {
    return this.isDial && !this.hasBands;
  }

  get scaleMin(): string {
    const set = this.block.reportType === 'kpi-score'
      ? this.block.reportExtraData.scaleMin
      : undefined;
    return Number.isFinite(set) ? String(set) : '';
  }

  setScaleMin(value: string) {
    const min = Number(value);
    this.patch.emit({ scaleMin: value.trim() === '' || !Number.isFinite(min) ? undefined : min });
  }

  // -------------------------------------------------------------- themes over a battery's rows
  //
  // Authored in two halves: the themes are named first, then each charted question is put in one.
  // The alternative -- a tick list of questions inside every theme -- lets the same row be ticked
  // twice, which is a state the chart cannot draw and the author cannot see.

  /** Only where there are rows to group, and more than one of them. */
  get showsCategories(): boolean {
    return this.applies('categories') && this.categoryRows.length > 1;
  }

  get categories(): { label: string; items: string[] }[] {
    const data = this.block.reportExtraData as { categories?: { label: string; items: string[] }[] };
    return data.categories ?? [];
  }

  /**
   * The rows to assign, each with the key a category stores and the label to show beside it.
   *
   * The key, not the label: a head-to-head's rows are all the same question worded once -- which is
   * why that module does not print it -- so labels there are identical and one of them cannot
   * identify a row. The label is shown, and falls back to the key where the question is unmapped.
   */
  get categoryRows(): { key: string; label: string }[] {
    return this.chartedKeys
      .filter(key => !!key)
      .map(key => ({
        key,
        label: this.variables.find(variable => variable.key === key)?.label ?? key,
      }));
  }

  /** The category a row is in, as an index into `categories`, or '' for none. */
  categoryOf(key: string): string {
    const at = this.categories.findIndex(category => category.items?.includes(key));
    return at < 0 ? '' : String(at);
  }

  get categoryOptions(): ReportSelectOption[] {
    return [
      { value: '', text: 'No category' },
      ...this.categories.map((category, at) => ({
        value: String(at),
        text: category.label.trim() || `Category ${at + 1}`,
      })),
    ];
  }

  addCategory() {
    this.patch.emit({ categories: [...this.categories, { label: '', items: [] }] });
  }

  removeCategory(index: number) {
    this.patch.emit({ categories: this.categories.filter((_, at) => at !== index) });
  }

  setCategoryLabel(index: number, label: string) {
    this.patch.emit({
      categories: this.categories.map((category, at) =>
        at === index ? { ...category, label } : category),
    });
  }

  /** Moves a row into one category, taking it out of every other: a row belongs to one thing. */
  setRowCategory(row: string, index: string) {
    const target = index === '' ? -1 : Number(index);
    this.patch.emit({
      categories: this.categories.map((category, at) => ({
        ...category,
        items: at === target
          ? [...(category.items ?? []).filter(item => item !== row), row]
          : (category.items ?? []).filter(item => item !== row),
      })),
    });
  }

  get netThreshold(): string {
    const threshold = this.block.reportType === 'stacked-scale'
      ? this.block.reportExtraData.netThreshold
      : undefined;
    return threshold === undefined ? '' : String(threshold);
  }

  /**
   * Blank means the default rather than zero: a threshold of nought would paint every positive net
   * green, which is a different claim from having no opinion about where the line sits.
   */
  setNetThreshold(value: string) {
    const threshold = Math.round(Number(value));
    this.patch.emit({
      netThreshold: value.trim() && Number.isFinite(threshold) ? threshold : DEFAULT_NET_THRESHOLD,
    });
  }

  get showsDropOff(): boolean {
    return this.applies('showDropOff');
  }

  /** Absent means on: the drop-off is the number a funnel is usually drawn to make. */
  get dropOffOn(): boolean {
    return this.block.reportType !== 'funnel' || this.block.reportExtraData.showDropOff !== false;
  }

  /**
   * The metric's answers, as the block sees them. A question has several and a single figure has
   * room for one, so this is the control that decides which -- shared by every module that reduces
   * a question to one number, and printing the list back is what stops "the first answer as asked"
   * being an invisible assumption.
   */
  get showsCountedAnswers(): boolean {
    if (!this.applies('countedAnswers') || this.answerChoices.length < 2) return false;
    // A funnel drawn from one question already has one figure per stage -- its "answers" are the
    // stages themselves, and ticking them would mean something else entirely.
    return this.block.reportType !== 'funnel' || this.knownSeries.length > 1;
  }

  private get knownSeries(): ChartSeries[] {
    return this.series.filter(entry => entry.known);
  }

  get answerChoices(): string[] {
    // Every series carries the same answer list, so the fullest of them speaks for all.
    const widest = this.knownSeries.reduce<ChartSeries | null>(
      (best, entry) => entry.points.length > (best?.points.length ?? 0) ? entry : best, null);
    return (widest?.points ?? []).filter(point => point.optionIndex >= 0).map(point => point.label);
  }

  private get namedAnswers(): string[] | undefined {
    if (this.block.reportType !== 'segment-tiles' && this.block.reportType !== 'funnel') return undefined;
    return this.block.reportExtraData.countedAnswers;
  }

  isAnswerCounted(answer: string): boolean {
    const named = this.namedAnswers;
    // Matches countedPoints: unset means the first answer, and an empty list means none.
    return named ? named.includes(answer) : answer === this.answerChoices[0];
  }

  countedAnswerLabel(): string {
    const counted = this.answerChoices.filter(answer => this.isAnswerCounted(answer));
    return counted.length ? `Counting ${counted.join(' + ')}` : 'Nothing counted -- tick at least one answer';
  }

  /**
   * Unticking every answer is allowed here, unlike the lists that pick what is drawn: it is how
   * someone switches from one answer to another, and refusing it left the box looking unticked while
   * the figure still counted it -- so the next tick was summed with an answer nobody could see.
   */
  toggleAnswer(answer: string, include: boolean) {
    const counted = new Set(this.answerChoices.filter(choice => this.isAnswerCounted(choice)));
    if (include) counted.add(answer); else counted.delete(answer);
    this.patch.emit({ countedAnswers: this.answerChoices.filter(choice => counted.has(choice)) });
  }

  /**
   * Anything that draws a chart has labels worth reclaiming the space of -- except the dial, which
   * has no answers on it. It reads the same `hideLabels` field for the line explaining its score,
   * and that has its own control beside the rest of the dial's settings, where the wording can say
   * what it actually hides. Two controls on one field is how a setting ends up being fought over.
   */
  get showsHideLabels(): boolean {
    return this.chartedKeys.length > 0 && this.applies('hideLabels') && !this.isDial;
  }

  get hideLabels(): boolean {
    const data = this.block.reportExtraData as { hideLabels?: boolean };
    return !!data.hideLabels;
  }

  /**
   * Only where a zero can appear on the axis: one question, drawn in asked order or banded. A sorted
   * or trimmed chart is a ranking and has already left them out, so the box would do nothing.
   */
  get showsEmptyAnswers(): boolean {
    if (this.block.reportType !== 'bar-chart' || this.chartedKeys.length !== 1) return false;
    const extra = this.block.reportExtraData;
    return !!extra.bands?.length || (!extra.sortDescending && !extra.topN);
  }

  get showEmptyAnswers(): boolean {
    return this.block.reportType !== 'bar-chart' || !this.block.reportExtraData.hideEmptyAnswers;
  }

  /** 0 rather than undefined: a patch skips undefined, and applySettings already reads 0 as "all". */
  setTopN(value: string) {
    const limit = Math.floor(Number(value));
    this.patch.emit({ topN: Number.isFinite(limit) && limit > 0 ? limit : 0 });
  }

  /**
   * Modules with no question to pick: their content is typed on the module itself. Derived from the
   * charted-key list rather than named types, so a new one of these needs nothing here.
   */
  get chartsNothing(): boolean {
    return this.chartedKeys.length === 0;
  }

  get isDial(): boolean {
    return this.block.reportType === 'kpi-score';
  }

  /** Blank rather than 0, so the field reads as "the default" when nothing is set. */
  get scaleMax(): string {
    const max = 'scaleMax' in this.block.reportExtraData ? this.block.reportExtraData.scaleMax : undefined;
    return max && max > 0 ? String(max) : '';
  }

  setScaleMax(value: string) {
    const max = Math.floor(Number(value));
    this.patch.emit({ scaleMax: Number.isFinite(max) && max > 0 ? max : 0 });
  }

  /** The module's own name, for the labels on its controls. */
  get label(): string {
    if (this.chartsNothing) return REPORT_BLOCK_LABELS[this.block.reportType];
    // A grid module is named by its question: the first row's label is one attribute of twenty, and
    // "Edit module settings for Industry leaders" names the wrong thing.
    if (this.takesGrid && this.chartedGrid) return this.chartedGrid;
    return this.labelFor(this.chartedKeys[0]) || 'this module';
  }

  labelFor(key: string): string {
    return this.variables.find(variable => variable.key === key)?.label ?? key;
  }

  private entryFor(key: string): PartVariable {
    const variable = this.variables.find(candidate => candidate.key === key);
    // A question not yet in variableMap needs its data position recorded, or it renders "No data".
    return { key, position: variable && !variable.mapped ? variable.position : undefined };
  }

  setVariable(key: string) {
    if (key) this.variableChange.emit(this.entryFor(key));
  }

  setPanel(index: number, key: string) {
    if (!key) return;
    const keys = [...this.chartedKeys];
    // Silently ignoring a duplicate would look broken; leaving the select on its old value is the
    // honest outcome, and the change detector puts it back.
    if (keys.includes(key)) return;
    keys[index] = key;
    this.variablesChange.emit(keys.map(candidate => this.entryFor(candidate)));
  }

  addPanel(key: string) {
    if (!key || this.chartedKeys.includes(key)) return;
    this.variablesChange.emit([...this.chartedKeys, key].map(candidate => this.entryFor(candidate)));
  }

  removePanel(key: string) {
    const keys = this.chartedKeys.filter(candidate => candidate !== key);
    if (!keys.length) return;
    this.variablesChange.emit(keys.map(candidate => this.entryFor(candidate)));
  }

  /**
   * The one question whose items this block is charting, when that is what it is doing -- either by
   * naming the question itself, or by naming some of its items and nothing else. Null for a chart
   * that mixes questions, which is a list of separate things and stays a list of dropdowns.
   */
  get itemsQuestion(): PickableVariable | null {
    if (!this.takesPanels || !this.chartedKeys.length) return null;

    const chosen = this.chartedKeys.map(key => this.variables.find(v => v.key === key));
    if (chosen.some(variable => !variable)) return null;

    // A column reports its parent; a question reports itself.
    const parents = new Set(chosen.map(variable => variable!.partOf ?? variable!.position));
    if (parents.size !== 1) return null;

    const [parent] = [...parents];
    const question = this.variables.find(v => v.position === parent && !v.partOf) ?? null;
    // An ordinary question has no items to tick, so it is not this interaction -- without this a
    // plain single-select bar chart would show an empty "Options shown" list instead of its picker.
    return question && this.variables.some(v => v.partOf === parent) ? question : null;
  }

  /** Its items, in the order the survey asked them, so unticking one never reorders the rest. */
  get itemChoices(): PickableVariable[] {
    const question = this.itemsQuestion;
    return question ? this.variables.filter(v => v.partOf === question.position) : [];
  }

  /** Naming the question itself, rather than listing items, means every item is in. */
  private get chartingWholeQuestion(): boolean {
    const question = this.itemsQuestion;
    return !!question && this.chartedKeys.includes(question.key);
  }

  isItemIncluded(item: PickableVariable): boolean {
    return this.chartingWholeQuestion || this.chartedKeys.includes(item.key);
  }

  itemCountLabel(): string {
    const total = this.itemChoices.length;
    const shown = this.itemChoices.filter(item => this.isItemIncluded(item)).length;
    return shown === total ? `All ${total} options` : `${shown} of ${total} options`;
  }

  /** The ownership matrix draws a matrix of one battery, optionally ranked by a second. */
  get takesMatrix(): boolean {
    return this.block.reportType === 'ownership-matrix';
  }

  /** The ranking grid behind the priority column, named by the question rather than by its rows. */
  get priorityGrid(): string {
    if (this.block.reportType !== 'ownership-matrix') return '';
    const keys = this.block.reportExtraData.priorityKeys ?? [];
    const groups = new Set(keys
      .map(key => this.variables.find(variable => variable.key === key)?.group ?? ''));
    return groups.size === 1 ? [...groups][0] : '';
  }

  setPriorityGrid(group: string) {
    const rows = this.variables.filter(v => v.group === group && !v.partOf);
    if (rows.length) this.priorityChange.emit(rows.map(row => this.entryFor(row.key)));
  }

  get showWhiteSpace(): boolean {
    return this.block.reportType === 'ownership-matrix'
      && this.block.reportExtraData.showWhiteSpace !== false;
  }

  get rowOrder(): string {
    return this.block.reportType === 'ownership-matrix'
      ? this.block.reportExtraData.rowOrder ?? 'priority'
      : 'priority';
  }

  readonly rowOrderOptions: ReportSelectOption[] = [
    { value: 'priority', text: 'Most important attribute first' },
    { value: 'asked', text: 'The order the survey asked them' },
  ];

  /**
   * The scatter picks one question per axis, so its questions are two labelled choices rather than
   * a list -- "which two questions" and "which is horizontal" are the same decision here, and a
   * generic add/remove list answers neither.
   */
  get takesAxes(): boolean {
    return this.block.reportType === 'scatter-plot';
  }

  axisKey(index: number): string {
    return this.chartedKeys[index] ?? '';
  }

  /** Reuses setPanel, which also refuses the same question twice -- two identical axes plot a line. */
  setAxis(index: number, key: string) {
    this.setPanel(index, key);
  }

  get xLabel(): string {
    return this.block.reportType === 'scatter-plot'
      ? this.block.reportExtraData.xLabel ?? ''
      : '';
  }

  get yLabel(): string {
    return this.block.reportType === 'scatter-plot'
      ? this.block.reportExtraData.yLabel ?? ''
      : '';
  }

  /** Nothing is substituted when these are blank, so the placeholder is a prompt, not a preview. */
  readonly axisPlaceholders: [string, string] = ['Horizontal axis title', 'Vertical axis title'];

  get showQuadrants(): boolean {
    return this.block.reportType === 'scatter-plot'
      && this.block.reportExtraData.showQuadrants !== false;
  }

  get quadrantLabel(): string {
    return this.block.reportType === 'scatter-plot'
      ? this.block.reportExtraData.quadrantLabel ?? ''
      : '';
  }

  /**
   * Ranked driver picks a whole ranking grid rather than its rows one at a time.
   *
   * Scoped to this module deliberately. A stacked distribution legitimately charts three questions
   * from three different grids, so a row is the right unit to pick there. A ranked driver's figures
   * only mean anything across one grid -- the first-place shares total 100 over *its* attributes and
   * nothing else -- so the grid is the unit, and picking rows one at a time is a way to build a
   * wrong chart one step at a time.
   */
  get takesGrid(): boolean {
    return this.block.reportType === 'tiered-ranking';
  }

  /** One choice per coded grid, named by the question rather than by any of its rows. */
  get gridOptions(): ReportSelectOption[] {
    const grids = new Map<string, PickableVariable>();
    for (const variable of this.chartable) {
      if (variable.partOf || !variable.group || variable.kind !== 'coded') continue;
      if (!grids.has(variable.group)) grids.set(variable.group, variable);
    }
    // The group already reads "Q21 — Please rank the following…", so it carries its own code.
    return [...grids.keys()].map(group => ({ value: group, text: group }));
  }

  /** The grid this block is charting, or blank while its rows come from more than one. */
  get chartedGrid(): string {
    if (!this.chartedKeys.length) return '';
    const groups = new Set(this.chartedKeys
      .map(key => this.variables.find(variable => variable.key === key)?.group ?? ''));
    return groups.size === 1 ? [...groups][0] : '';
  }

  /** Its rows, in the order the survey asked them, so unticking one never reorders the rest. */
  get gridRows(): PickableVariable[] {
    const grid = this.chartedGrid;
    return grid ? this.variables.filter(v => v.group === grid && !v.partOf) : [];
  }

  /** Every row of the grid, since a ranking is only a ranking across all of them. */
  setGrid(group: string) {
    const rows = this.variables.filter(v => v.group === group && !v.partOf);
    if (!rows.length) return;
    this.variablesChange.emit(rows.map(row => this.entryFor(row.key)));
  }

  isGridRowIncluded(row: PickableVariable): boolean {
    return this.chartedKeys.includes(row.key);
  }

  /**
   * Dropping a row is offered because a deck sometimes reports part of a grid, but it is the
   * exception: the shares stop totalling a ranking as soon as one is out, which is why the module
   * only complains about a total that is too *high*.
   */
  toggleGridRow(row: PickableVariable, box: HTMLInputElement) {
    const included = new Set(this.chartedKeys);
    if (box.checked) included.add(row.key); else included.delete(row.key);
    if (!included.size) return keepTicked(box);

    this.variablesChange.emit(this.gridRows
      .filter(candidate => included.has(candidate.key))
      .map(candidate => this.entryFor(candidate.key)));
  }

  gridRowCountLabel(): string {
    const total = this.gridRows.length;
    const shown = this.gridRows.filter(row => this.isGridRowIncluded(row)).length;
    return shown === total ? `All ${total} attributes` : `${shown} of ${total} attributes`;
  }

  toggleItem(item: PickableVariable, box: HTMLInputElement) {
    const question = this.itemsQuestion;
    if (!question) return;

    const items = this.itemChoices;
    const included = new Set(items.filter(candidate => this.isItemIncluded(candidate))
      .map(candidate => candidate.key));
    if (box.checked) included.add(item.key); else included.delete(item.key);
    // A chart of nothing is not a state worth being able to reach by unticking.
    if (!included.size) return keepTicked(box);

    // Back to every item: say so with the question's own key rather than listing all of them, so
    // the design keeps the question's title and base instead of becoming an anonymous list.
    const keys = included.size === items.length
      ? [question.key]
      : items.filter(candidate => included.has(candidate.key)).map(candidate => candidate.key);
    this.variablesChange.emit(keys.map(key => this.entryFor(key)));
  }

  setGroupBy(key: string) {
    if (key) this.groupByChange.emit(this.entryFor(key));
  }

  /** "One panel per" is wrong for a module whose panels are tiles on a map, or columns of a table. */
  get groupByLabel(): string {
    if (this.block.reportType === 'segment-tiles') return 'One tile per';
    if (this.block.reportType === 'crosstab') return 'One column per';
    return 'One panel per';
  }

  /** Tiles, columns or panels -- the control underneath is the same choice either way. */
  get panelWord(): string {
    if (this.block.reportType === 'segment-tiles') return 'Tiles';
    if (this.block.reportType === 'crosstab') return 'Columns';
    return 'Panels';
  }

  /** Lower-case, for mid-sentence use on the Total checkbox. */
  get panelNoun(): string {
    return this.panelWord.slice(0, -1).toLowerCase();
  }

  /**
   * What a cell reads as. A table is the only module with a cell rather than a mark, so this is the
   * only place the choice exists -- a bar's length already says which of the two it is showing.
   */
  readonly cellValueOptions: ReportSelectOption[] = [
    { value: 'percent', text: 'Percentage of the column' },
    { value: 'count', text: 'Number of respondents' },
  ];

  get cellValue(): string {
    return this.block.reportType === 'crosstab'
      ? this.block.reportExtraData.cellValue ?? 'percent'
      : 'percent';
  }

  get showCounts(): boolean {
    return this.block.reportType === 'crosstab' && !!this.block.reportExtraData.showCounts;
  }

  /**
   * Which pole is drawn on the left. Offered with the answers named rather than as "first"/"last",
   * because a survey lists a pair either way round and the datamap does not record which -- read
   * the wrong way this does not degrade, it reverses, and the bar says the opposite of the finding.
   */
  get showsLeftAnswer(): boolean {
    return this.block.reportType === 'diverging-bars' && !!this.chartedKeys.length;
  }

  get leftAnswer(): string {
    return this.block.reportType === 'diverging-bars'
      ? this.block.reportExtraData.leftAnswer ?? 'first'
      : 'first';
  }

  get leftAnswerOptions(): ReportSelectOption[] {
    const [first, last] = this.poleAnswers;
    return [
      { value: 'first', text: first ? `${first} on the left` : 'The first answer as asked' },
      { value: 'last', text: last ? `${last} on the left` : 'The last answer as asked' },
    ];
  }

  get leftAnswerHint(): string {
    const [first, last] = this.poleAnswers;
    if (!first || !last) return 'The two ends of the bar are the first and last answers as asked.';
    return `Drawing "${this.leftAnswer === 'last' ? last : first}" on the left and `
      + `"${this.leftAnswer === 'last' ? first : last}" on the right.`;
  }

  /** The first and last answers of the first row, which is what the control can name. */
  private get poleAnswers(): [string, string] {
    const asked = (this.rankingSeries.find(s => s.known && s.points.length)?.points ?? [])
      .filter(point => point.optionIndex >= 0);
    if (asked.length < 2) return ['', ''];
    return [asked[0].label, asked[asked.length - 1].label];
  }

  get showGap(): boolean {
    return this.block.reportType === 'diverging-bars'
      && this.block.reportExtraData.showGap !== false;
  }


  /**
   * Ranked driver. Its settings are all about how a ranking is *read* rather than what is drawn from
   * it -- how many places count as "top", which end of the list is first place, how the sorted rows
   * are banded -- so every one of them is offered against the answers the question actually has.
   */
  get showsRankedDriver(): boolean {
    return this.block.reportType === 'tiered-ranking' && !!this.chartedKeys.length;
  }

  /**
   * The rank positions the grid asked, counted across every row. Same rule as the block: aggregate()
   * drops an option nobody chose, so one row can be short of a rank that the grid still has.
   */
  private get rankCount(): number {
    const indexes = this.rankingSeries
      .filter(s => s.known && !s.multiResponse)
      .flatMap(s => s.points.map(point => point.optionIndex))
      .filter(index => index >= 0);
    return indexes.length ? Math.max(...indexes) + 1 : 0;
  }

  /** Top N of N ranks is everyone, so the last place is never offerable. */
  get topRanksMax(): number {
    return Math.max(1, this.rankCount - 1);
  }

  /**
   * The rows a ranking is read from. On an ownership matrix the series carry the attribute battery
   * first and the ranking grid behind it, so only the tail is the ranking -- an association row is
   * not a rank scale, and counting its answers would offer ranks the grid never asked.
   */
  private get rankingSeries(): ChartSeries[] {
    if (this.block.reportType !== 'ownership-matrix') return this.series;
    return this.series.slice(this.block.reportExtraData.variableKeys?.length ?? 0);
  }

  get topRanks(): number {
    const set = this.block.reportType === 'tiered-ranking' || this.block.reportType === 'ownership-matrix'
      ? this.block.reportExtraData.topRanks ?? DEFAULT_TOP_RANKS
      : DEFAULT_TOP_RANKS;
    return Math.min(Math.max(1, Math.trunc(set)), this.topRanksMax);
  }

  setTopRanks(value: string) {
    const ranks = Math.trunc(Number(value));
    if (!Number.isFinite(ranks) || ranks < 1) return;
    this.patch.emit({ topRanks: Math.min(ranks, this.topRanksMax) });
  }

  get rankOneEnd(): string {
    return this.block.reportType === 'tiered-ranking' || this.block.reportType === 'ownership-matrix'
      ? this.block.reportExtraData.rankOneEnd ?? 'first'
      : 'first';
  }

  /**
   * Named rather than offered as "first"/"last" wherever the survey titled its ends, which is the
   * usual shape -- "Trust the most" at one end, "Trust the least" at the other, and nothing in
   * between. Read the wrong way round this inverts the finding rather than degrading it.
   */
  get rankOneEndOptions(): ReportSelectOption[] {
    const [first, last] = this.rankEndAnswers;
    return [
      { value: 'first', text: first ? `${first} is first place` : 'The first answer as asked' },
      { value: 'last', text: last ? `${last} is first place` : 'The last answer as asked' },
    ];
  }

  get rankOneEndHint(): string {
    const [first, last] = this.rankEndAnswers;
    const best = this.rankOneEnd === 'last' ? last : first;
    if (!best) return 'First place is the first answer as asked, unless this says otherwise.';
    return `Counting "${best}" as first place, and the ${this.topRanks} places from it as the top.`;
  }

  /** The two ends of the answer list, which is all a rank scale usually titles. */
  private get rankEndAnswers(): [string, string] {
    const asked = (this.series.find(s => s.known && s.points.length)?.points ?? [])
      .filter(point => point.optionIndex >= 0);
    if (asked.length < 2) return ['', ''];
    return [asked[0].label, asked[asked.length - 1].label];
  }

  /**
   * How many bands the block will actually draw, which is what the control shows -- the stored
   * number clamped by the same rule the block clamps it by, so the dropdown cannot claim four tiers
   * over three attributes.
   */
  get tierCount(): number {
    const set = this.block.reportType === 'tiered-ranking'
      ? this.block.reportExtraData.tierCount
      : undefined;
    return tierCountFor(set, this.chartedKeys.length);
  }

  /** The dropdown trades in strings, and a template cannot reach the global String. */
  get tierCountValue(): string {
    return String(this.tierCount);
  }

  /** Capped at the attributes charted, so every offered choice draws the number it names. */
  get tierCountOptions(): ReportSelectOption[] {
    const most = Math.max(1, Math.min(MAX_TIERS, this.chartedKeys.length));
    return Array.from({ length: most }, (unused, index) => ({
      value: String(index + 1),
      text: index === 0 ? '1 — no tiers, one list' : `${index + 1} tiers`,
    }));
  }

  setTierCount(value: string) {
    const count = Math.trunc(Number(value));
    if (!Number.isFinite(count) || count < 1) return;
    this.patch.emit({ tierCount: Math.min(count, MAX_TIERS) });
  }

  /**
   * One input per tier the current settings actually produce, so the list grows and shrinks with
   * the number of attributes rather than offering names for bands that do not exist.
   */
  get tierNames(): { value: string; placeholder: string }[] {
    const count = this.tierCount;
    if (count <= 1) return [];
    const stored = this.block.reportType === 'tiered-ranking'
      ? this.block.reportExtraData.tierLabels ?? []
      : [];
    return Array.from({ length: count }, (unused, index) => ({
      value: stored[index] ?? '',
      placeholder: DEFAULT_TIER_LABELS[index] ?? `Tier ${index + 1}`,
    }));
  }

  /** Patched whole: the names are positional, so a partial list would rename every tier below. */
  setTierLabel(index: number, value: string) {
    const names = this.tierNames.map(tier => tier.value);
    names[index] = value;
    this.patch.emit({ tierLabels: names });
  }

  /**
   * How many answers to a page. Pages rather than a scroll box: the deck is read top to bottom and
   * printed, and an inner scrollbar loses everything past the first screen when it is.
   */
  get pageSize(): string {
    const size = this.block.reportType === 'open-end-explorer'
      ? this.block.reportExtraData.pageSize
      : undefined;
    return size && size > 0 ? String(size) : '';
  }

  setPageSize(value: string) {
    const size = Math.trunc(Number(value));
    this.patch.emit({ pageSize: Number.isFinite(size) && size > 0 ? size : 0 });
  }

  get searchable(): boolean {
    return this.block.reportType === 'open-end-explorer'
      && this.block.reportExtraData.searchable !== false;
  }

  get maxWords(): string {
    const words = this.block.reportType === 'word-cloud'
      ? this.block.reportExtraData.maxWords
      : undefined;
    return words && words > 0 ? String(words) : '';
  }

  setMaxWords(value: string) {
    const words = Math.trunc(Number(value));
    this.patch.emit({ maxWords: Number.isFinite(words) && words > 0 ? words : 0 });
  }

  get minCount(): string {
    const least = this.block.reportType === 'word-cloud'
      ? this.block.reportExtraData.minCount
      : undefined;
    return least && least > 0 ? String(least) : '';
  }

  setMinCount(value: string) {
    const least = Math.trunc(Number(value));
    this.patch.emit({ minCount: Number.isFinite(least) && least > 0 ? least : 0 });
  }

  /**
   * The question's own words are dropped by default, because respondents echo the question back and
   * those words are then the largest in the cloud while carrying no finding. The control is worded
   * the way it reads on the card rather than as the stored flag.
   */
  get useQuestionWords(): boolean {
    return this.block.reportType === 'word-cloud'
      && !!this.block.reportExtraData.useQuestionWords;
  }

  /** Free text, comma separated: a list of words is what someone types, not a set they build. */
  get stopwords(): string {
    const words = this.block.reportType === 'word-cloud'
      ? this.block.reportExtraData.stopwords ?? []
      : [];
    return words.join(', ');
  }

  setStopwords(value: string) {
    this.patch.emit({
      stopwords: value.split(',').map(word => word.trim()).filter(word => !!word),
    });
  }

  get showsCellValue(): boolean {
    return this.block.reportType === 'crosstab';
  }

  /** Nothing to add under a cell that is already the count. */
  get showsCountToggle(): boolean {
    return this.block.reportType === 'crosstab' && this.cellValue !== 'count';
  }

  /** The table's own settings, once it has a question for its rows. */
  get showsTableStats(): boolean {
    return this.block.reportType === 'crosstab' && !!this.chartedKeys[0];
  }

  private get crosstab(): CrosstabExtraData | null {
    return this.block.reportType === 'crosstab' ? this.block.reportExtraData : null;
  }

  readonly sigTestOptions: ReportSelectOption[] = [
    { value: 'none', text: 'Off' },
    { value: 'columns', text: 'Compare columns -- letters mark a figure higher than another column' },
    { value: 'total', text: 'Compare with everyone else -- arrows mark a figure above or below' },
  ];

  get sigTest(): string {
    return this.crosstab?.sigTest ?? 'none';
  }

  /** The report-level side of the tests, printed so nobody looks for it here. */
  get statisticsLine(): string {
    const resolved = resolveStatistics(this.statistics);
    const weighting = resolved.weighting?.method === 'column'
      ? `weighted by ${resolved.weighting.column}`
      : resolved.weighting ? 'weighted to targets' : 'unweighted';
    const correction = resolved.correction === 'none' ? '' : resolved.correction === 'bh' ? ' · Benjamini-Hochberg' : ' · Bonferroni';
    return `${resolved.confidenceLevel}% confidence${correction} · bases under ${resolved.minBase} not tested · ${weighting}`;
  }

  /** The answers down the table's side, which is what a net or a mean can be made of. */
  get rowAnswers(): string[] {
    const key = this.chartedKeys[0];
    return key ? this.optionsOf(key) : [];
  }

  /** A scale has codes worth averaging; a multi-punch question or a battery does not. */
  get takesMean(): boolean {
    const key = this.chartedKeys[0];
    return this.variables.find(variable => variable.key === key)?.kind === 'coded';
  }

  get nets(): { label: string; answers: string[] }[] {
    return this.crosstab?.nets ?? [];
  }

  /** Top and bottom two, offered once there are enough answers for a scale to have two ends. */
  get netPresets(): { label: string; answers: string[] }[] {
    if (!this.takesMean) return [];
    const offScale = new Set(this.offScale);
    const answers = this.rowAnswers.filter(answer => !offScale.has(answer));
    if (answers.length < 4) return [];
    const first = answers.slice(0, 2);
    const last = answers.slice(-2);
    return [
      { label: `${first.join(' + ')}`, answers: first },
      { label: `${last.join(' + ')}`, answers: last },
    ];
  }

  /** The row question's answers that are off its scale -- "Don't know" and the like. */
  private get offScale(): string[] {
    const key = this.chartedKeys[0];
    const variable = this.layoutOf?.(key);
    return variable ? offScaleAnswers(variable) : [];
  }

  /** Turning the mean on pre-ticks the answers plainly off the scale, so "Don't know" is not averaged. */
  setShowMean(on: boolean) {
    const patch: BlockPatch = { showMean: on };
    if (on && !this.crosstab?.meanExcludes) patch.meanExcludes = this.offScale;
    this.patch.emit(patch);
  }

  addNet(preset?: { label: string; answers: string[] }) {
    const label = preset ? preset.label : `Net ${this.nets.length + 1}`;
    this.patch.emit({ nets: [...this.nets, { label, answers: preset?.answers ?? [] }] });
  }

  renameNet(index: number, label: string) {
    this.patch.emit({
      nets: this.nets.map((net, i) => i === index ? { ...net, label: label.trim() || net.label } : net),
    });
  }

  toggleNetAnswer(index: number, answer: string, on: boolean) {
    this.patch.emit({
      nets: this.nets.map((net, i) => i !== index ? net : {
        ...net,
        // Kept in the answers' own order, so the net reads the way the scale does.
        answers: this.rowAnswers.filter(a => a === answer ? on : net.answers.includes(a)),
      }),
    });
  }

  removeNet(index: number) {
    this.patch.emit({ nets: this.nets.filter((_, i) => i !== index) });
  }

  get showMean(): boolean {
    return !!this.crosstab?.showMean;
  }

  /** As the table applies them: the ones chosen, or, until any are, the answers plainly off the scale. */
  get meanExcludes(): string[] {
    return this.crosstab?.meanExcludes ?? this.offScale;
  }

  toggleMeanExclude(answer: string, excluded: boolean) {
    this.patch.emit({
      meanExcludes: this.rowAnswers.filter(a => a === answer ? excluded : this.meanExcludes.includes(a)),
    });
  }

  readonly shadingOptions: ReportSelectOption[] = [
    { value: 'none', text: 'Plain' },
    { value: 'heat', text: 'Heat -- larger figures are shaded deeper' },
    { value: 'significance', text: 'Significance -- green above everyone else, red below' },
  ];

  get shading(): string {
    return this.crosstab?.shading ?? 'none';
  }

  /** The "tables for more questions" picker: open or shut, what is searched for, what is ticked. */
  bulkOpen = false;
  /** Said once the copies are made, where a screen reader will hear it. */
  bulkDone = '';
  bulkQuery = '';
  readonly bulkPicked = new Set<string>();

  /** Every question a table could have down its side, bar the one this table already has. */
  get bulkChoices(): PickableVariable[] {
    const needle = this.bulkQuery.trim().toLowerCase();
    return this.variables.filter(variable => !variable.partOf && variable.key !== this.chartedKeys[0]
      && (variable.kind === 'coded' || variable.kind === 'multi' || variable.kind === 'numeric')
      && (!needle || `${variable.position} ${variable.label}`.toLowerCase().includes(needle)));
  }

  toggleBulk(key: string, on: boolean) {
    if (on) this.bulkPicked.add(key);
    else this.bulkPicked.delete(key);
  }

  /** Ticks every question the search shows, or unticks them if they are all ticked already. */
  toggleAllBulk() {
    const shown = this.bulkChoices.map(variable => variable.key);
    const all = shown.every(key => this.bulkPicked.has(key));
    shown.forEach(key => all ? this.bulkPicked.delete(key) : this.bulkPicked.add(key));
  }

  get allBulkPicked(): boolean {
    const shown = this.bulkChoices;
    return !!shown.length && shown.every(variable => this.bulkPicked.has(variable.key));
  }

  createBulk() {
    // In the survey's own order rather than the order they were ticked in.
    const keys = this.variables.filter(variable => this.bulkPicked.has(variable.key)).map(variable => variable.key);
    if (!keys.length) return;
    this.copyForQuestions.emit(keys.map(key => this.entryFor(key)));
    this.bulkPicked.clear();
    this.bulkQuery = '';
    this.bulkOpen = false;
    this.bulkDone = `Created ${keys.length} table${keys.length === 1 ? '' : 's'} under this one.`;
    // The button pressed was inside the panel just closed; focus goes back to the one that opens it.
    setTimeout(() => document.getElementById(`bulk-toggle-${this.block.id}`)?.focus());
  }

  get layerBy(): string {
    return this.crosstab?.layerBy ?? '';
  }

  /** Any question that splits respondents and is not already on an axis of the table. */
  get layerOptions(): ReportSelectOption[] {
    const onAxes = new Set([this.groupByKey, ...this.subgroups.map(subgroup => subgroup.variableKey)]);
    return [
      { value: '', text: 'None' },
      ...this.groupByChoices.filter(variable => !onAxes.has(variable.key)).map(toOption),
    ];
  }

  setLayer(key: string) {
    this.layerChange.emit(key ? this.entryFor(key) : null);
  }

  readonly layerViewOptions: ReportSelectOption[] = [
    { value: 'nested', text: 'Nested -- the columns repeated under each answer, in one table' },
    { value: 'tabs', text: 'Tabs -- one table per answer, a tab to switch between them' },
    { value: 'grid', text: 'Grid -- a small table per answer, side by side' },
  ];

  get layerView(): string {
    return this.crosstab?.layerView ?? 'nested';
  }

  get tableFilter(): { variableKey: string; answer: string }[] {
    return this.crosstab?.tableFilter ?? [];
  }

  /** The filter, question by question, with every answer the question offers to tick. */
  /** Questions added to the filter whose answers are not ticked yet. They filter nothing until they are. */
  pendingFilterKeys: string[] = [];

  get tableFilterGroups(): { variableKey: string; label: string; answers: string[] }[] {
    const keys = [...new Set([...this.tableFilter.map(entry => entry.variableKey), ...this.pendingFilterKeys])];
    return keys.map(variableKey => ({
      variableKey,
      label: this.labelFor(variableKey),
      answers: this.optionsOf(variableKey),
    }));
  }

  get tableFilterOptions(): ReportSelectOption[] {
    const chosen = new Set(this.tableFilter.map(entry => entry.variableKey));
    return this.groupByChoices.filter(variable => !chosen.has(variable.key)).map(toOption);
  }

  isFilteredIn(variableKey: string, answer: string): boolean {
    return this.tableFilter.some(entry => entry.variableKey === variableKey && entry.answer === answer);
  }

  /** Starts with the first answer ticked: a filter that lets nobody through is not a useful start. */
  addTableFilterQuestion(variableKey: string) {
    if (!variableKey || this.pendingFilterKeys.includes(variableKey)) return;
    this.pendingFilterKeys = [...this.pendingFilterKeys, variableKey];
  }

  toggleTableFilter(variableKey: string, answer: string, box: HTMLInputElement) {
    const rest = this.tableFilter.filter(e => !(e.variableKey === variableKey && e.answer === answer));
    const next = box.checked ? [...this.tableFilter, { variableKey, answer }] : rest;
    // Unticking a question's last answer leaves it in the list, waiting: removing it is a separate,
    // deliberate action, and a question filtering to nobody is never a state the table reaches.
    if (!box.checked && !next.some(entry => entry.variableKey === variableKey)) {
      this.pendingFilterKeys = [...this.pendingFilterKeys, variableKey];
    } else if (box.checked) {
      this.pendingFilterKeys = this.pendingFilterKeys.filter(key => key !== variableKey);
    }
    this.emitTableFilter(next);
  }

  isFilterPending(variableKey: string): boolean {
    return this.pendingFilterKeys.includes(variableKey);
  }

  removeTableFilterQuestion(variableKey: string) {
    this.pendingFilterKeys = this.pendingFilterKeys.filter(key => key !== variableKey);
    this.emitTableFilter(this.tableFilter.filter(entry => entry.variableKey !== variableKey));
  }

  private emitTableFilter(next: { variableKey: string; answer: string }[]) {
    const keys = [...new Set(next.map(entry => entry.variableKey))];
    this.tableFilterChange.emit({ tableFilter: next, variables: keys.map(key => this.entryFor(key)) });
  }

  /**
   * Which answers become panels, question by question.
   *
   * Stored as `subgroups` -- a question and an answer per panel -- which is flat on purpose: the
   * renderer groups by it in order, and the panels of one deck genuinely can come from more than
   * one question. "Female", "Male" from gender and "45+" from age is one list of three, not two
   * lists that have to be reconciled.
   *
   * An empty list means the grouping question's answers, all of them. That is what a block starts
   * as, and it stays that way until something is unticked or a second question is added -- so a
   * later data refresh that adds an answer is picked up rather than frozen out.
   */
  get showsSubgroups(): boolean {
    return this.applies('subgroups') && !!this.groupByKey;
  }

  get subgroups(): { variableKey: string; answer: string }[] {
    // Narrowed through a local so the three checks are written once. Every module `fieldAppliesTo`
    // admits for 'subgroups' has to be here: a module missing from this list reads as having no
    // panels chosen at all, which is not a smaller bug than it sounds -- `isPanelIncluded` then
    // reports every answer of the grouping question as ticked whatever is stored, and
    // `materialised` rebuilds from that, so an untick is drawn as still ticked and the next edit
    // silently restores the answer that was removed.
    const block = this.block;
    if (block.reportType !== 'subgroup-panels' && block.reportType !== 'segment-tiles'
      && block.reportType !== 'crosstab') {
      return [];
    }
    return block.reportExtraData.subgroups ?? [];
  }

  /**
   * The questions on offer, the grouping question first. A question is listed once it contributes a
   * panel, and the first one stays whether or not it does -- it is the block's own grouping choice,
   * and the dropdown above is what changes it.
   */
  get panelGroups(): { variableKey: string; label: string; answers: string[]; primary: boolean }[] {
    const keys = [this.groupByKey];
    for (const subgroup of this.subgroups) {
      if (!keys.includes(subgroup.variableKey)) keys.push(subgroup.variableKey);
    }
    return keys.map(variableKey => ({
      variableKey,
      label: this.labelFor(variableKey),
      // Read off the records, so an answer nobody gave is not listed: there is no panel to untick,
      // because there was never going to be one.
      answers: this.optionsOf(variableKey),
      primary: variableKey === this.groupByKey,
    }));
  }

  /** Questions not already contributing, so the same one cannot be added twice. */
  get addQuestionOptions(): ReportSelectOption[] {
    const chosen = new Set(this.panelGroups.map(group => group.variableKey));
    return this.groupByChoices.filter(variable => !chosen.has(variable.key)).map(toOption);
  }

  isPanelIncluded(variableKey: string, answer: string): boolean {
    const chosen = this.subgroups;
    if (chosen.length) {
      return chosen.some(s => s.variableKey === variableKey && s.answer === answer);
    }
    // Nothing stored means the grouping question's answers, all of them -- and nothing else.
    return variableKey === this.groupByKey;
  }

  panelCountLabel(): string {
    const groups = this.panelGroups;
    const total = groups.reduce((sum, group) => sum + group.answers.length, 0);
    if (!total) return 'None';
    const shown = groups.reduce(
      (sum, group) => sum + group.answers.filter(a => this.isPanelIncluded(group.variableKey, a)).length,
      0);
    return shown === total ? `All ${total}` : `${shown} of ${total}`;
  }

  togglePanel(variableKey: string, answer: string, box: HTMLInputElement) {
    const chosen = this.materialised();
    const accepted = this.emitSubgroups(box.checked
      ? [...chosen, { variableKey, answer }]
      : chosen.filter(s => !(s.variableKey === variableKey && s.answer === answer)));
    if (!accepted) keepTicked(box);
  }

  addPanelQuestion(variableKey: string) {
    if (!variableKey) return;
    // Every answer to begin with, which is what the grouping question does and what someone adding
    // a question means by adding it.
    const added = this.optionsOf(variableKey).map(answer => ({ variableKey, answer }));
    this.emitSubgroups([...this.materialised(), ...added]);
  }

  removePanelQuestion(variableKey: string) {
    this.emitSubgroups(this.materialised().filter(s => s.variableKey !== variableKey));
  }

  /**
   * The stored list with the "empty means every answer of the grouping question" shorthand spelled
   * out. Anything that adds a second question has to spell it out, or the first question's panels
   * would vanish the moment the list stopped being empty.
   */
  private materialised(): { variableKey: string; answer: string }[] {
    const chosen = this.subgroups;
    if (chosen.length) return chosen;
    return this.optionsOf(this.groupByKey).map(answer => ({ variableKey: this.groupByKey, answer }));
  }

  /** False where the list was refused, so a checkbox that asked for it can be put back. */
  private emitSubgroups(next: { variableKey: string; answer: string }[]): boolean {
    // A grid of nothing is not a state worth being able to reach by unticking.
    if (!next.length) return false;

    // Back to exactly the grouping question's answers: say so with an empty list rather than naming
    // them, so the block keeps following the question if a refresh adds an answer.
    const answers = this.optionsOf(this.groupByKey);
    const isDefault = next.length === answers.length
      && next.every(s => s.variableKey === this.groupByKey && answers.includes(s.answer));

    // Emitted with the questions it names, not as a plain patch: the list is the only place a
    // second question is recorded, so nothing else would ever put it in variableMap.
    const keys = [...new Set(next.map(subgroup => subgroup.variableKey))];
    this.subgroupsChange.emit({
      subgroups: isDefault ? [] : next,
      variables: keys.map(key => this.entryFor(key)),
    });
    return true;
  }

  /** Questions offered as the grouping variable, minus the ones this chart already draws. */
  get groupByChoices(): PickableVariable[] {
    const charted = new Set(this.chartedKeys);
    return this.variables.filter(variable => !charted.has(variable.key)
      && variable.kind !== 'numeric' && variable.kind !== 'text' && variable.kind !== 'heatmap'
      && !variable.partOf);
  }

  /**
   * Every option of the grouping question becomes another panel, and a circle chart of a
   * multi-answer question cannot work at all -- both worth saying before the chart is looked at.
   */
  get advice(): string {
    if (this.takesGroupBy) {
      const grouping = this.variables.find(variable => variable.key === this.groupByKey);
      if (!grouping) return 'Choose a grouping question to split this into panels.';
      const panels = grouping.optionCount + (this.includeTotal ? 1 : 0);
      return `${grouping.optionCount} options — ${panels} panels including Total.`;
    }

    const charted = this.variables.find(variable => variable.key === this.chartedKeys[0]);
    const circle = this.block.reportType === 'pie-chart' || this.block.reportType === 'donut-chart';
    if (!charted || !circle) return '';

    // Not gated on option count: a pie of a multi-answer question is wrong at any size, because its
    // options sum past 100%.
    if (charted.kind === 'multi') {
      return this.block.reportType === 'pie-chart'
        ? 'This question accepts several answers, which cannot be slices of one pie. Chart it as a Donut.'
        : `${charted.optionCount} options — renders as ${charted.optionCount} small donuts, one per option.`;
    }

    return charted.optionCount > CIRCLE_COMFORTABLE_OPTIONS
      ? `${charted.optionCount} options — a bar will read more clearly.`
      : '';
  }
}
